import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Ban, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
const thisMonth = () => new Date().toISOString().slice(0, 7);

// Minimal markdown: ## headings, bullets, **bold**.
const Md = ({ text }) => (
  <div className="space-y-2 text-sm leading-relaxed text-slate-300">
    {text.split("\n").map((line, i) => {
      const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => (seg.startsWith("**") ? <strong key={j} className="text-white">{seg.slice(2, -2)}</strong> : seg));
      if (/^##\s/.test(line)) return <h4 key={i} className="pt-2 font-display text-base font-semibold text-white">{line.replace(/^#+\s*/, "")}</h4>;
      if (/^[-*]\s/.test(line)) return <p key={i} className="flex gap-2"><span className="text-brand-red">•</span><span>{bold(line.replace(/^[-*]\s/, ""))}</span></p>;
      if (!line.trim()) return null;
      return <p key={i}>{bold(line)}</p>;
    })}
  </div>
);

export const DisqualifiedInsights = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (m) => {
    try { const r = await client.get("/insights/disqualified", { params: { month: m } }); setData(r.data); }
    catch (e) { toast.error(tr("Couldn't load disqualified insights")); }
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  const generate = async () => {
    setBusy(true);
    try { const r = await client.post("/insights/disqualified", null, { params: { month } }); setData((d) => ({ ...d, summary: r.data.summary, summary_stale: false })); toast.success(tr("Claude's read is ready")); }
    catch (e) { toast.error(e?.response?.status === 402 ? "Free AI credit used up" : "Couldn't generate", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const months = Array.from(new Set([thisMonth(), ...(data?.months || [])])).sort().reverse();
  const max = Math.max(1, ...(data?.reasons || []).map((r) => r.count));

  return (
    <section data-testid="disqualified-insights" className="rounded-2xl border border-white/[0.07] bg-card/40 p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-white"><Ban className="h-5 w-5 text-brand-red" /> {tr("Why deals got disqualified")}</h2>
          <p className="text-sm text-muted-foreground">{tr("Claude reads every disqualified deal and its reason, then hands you sharper sourcing rules for next month.")}</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="disq-month-select" className="h-10 w-48 border-white/12 bg-white/[0.03] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="glass-strong border-white/10">
            {months.map((m) => <SelectItem key={m} value={m} className="focus:bg-white/5">{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data && (
        <div className="mt-6 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-end gap-6">
              <div><p className="font-display text-4xl font-bold text-white" data-testid="disq-total">{data.total}</p><p className="text-xs uppercase tracking-widest text-muted-foreground">{tr("disqualified")}</p></div>
              <div><p className="font-display text-2xl font-semibold text-white/70">{data.active}</p><p className="text-xs uppercase tracking-widest text-muted-foreground">{tr("still active")}</p></div>
              {data.total + data.active > 0 && <div><p className="font-display text-2xl font-semibold text-brand-red">{Math.round((data.total / (data.total + data.active)) * 100)}%</p><p className="text-xs uppercase tracking-widest text-muted-foreground">{tr("kill rate")}</p></div>}
            </div>
            <div className="mt-5 space-y-2" data-testid="disq-reasons">
              {data.reasons.length === 0 && <p className="text-sm text-muted-foreground">{tr("Nothing disqualified in")} {monthLabel(month)}.</p>}
              {data.reasons.map((r) => (
                <div key={r.reason} data-testid={`disq-reason-${r.reason.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm"><span className="text-white">{r.reason}</span><span className="text-muted-foreground">{r.count}</span></div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-brand-red/80" style={{ width: `${(r.count / max) * 100}%` }} /></div>
                </div>
              ))}
            </div>
            {data.items.length > 0 && (
              <ul className="mt-5 max-h-48 space-y-1.5 overflow-y-auto pr-1 text-xs text-muted-foreground" data-testid="disq-items">
                {data.items.map((d) => <li key={d.id} className="flex justify-between gap-3"><span className="truncate text-slate-300">{d.title}</span><span className="shrink-0">{d.disqualifyReason}</span></li>)}
              </ul>
            )}
          </div>
          <div className="lg:col-span-7">
            <div className="relative h-full rounded-2xl border border-brand-red/20 bg-gradient-to-br from-brand-red/[0.08] to-transparent p-5">
              <div className="mb-3 flex items-center justify-between">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-red"><Sparkles className="h-3.5 w-3.5" /> {tr("Claude's read")}{data.summary_stale ? ` · ${tr("out of date")}` : ""}</p>
                <Button size="sm" onClick={generate} disabled={busy || data.total === 0} data-testid="disq-generate-btn" className="h-9 bg-brand-red text-white hover:bg-brand-redDark">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : data.summary ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />} {data.summary ? tr("Refresh") : tr("Summarise the month")}
                </Button>
              </div>
              {data.summary ? <div data-testid="disq-summary"><Md text={data.summary} /></div>
                : <p className="text-sm text-muted-foreground" data-testid="disq-summary-empty">{data.total ? tr("Hit “Summarise the month” and Claude will spot the patterns — overpriced districts, flaky sources, the lot.") : tr("Disqualify a few deals (swipe left, or the Disqualify button) and the patterns will show up here.")}</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
